import { type FormEvent, type KeyboardEvent, useRef, useState } from 'react';
import { AppBar } from '../components/AppBar';
import { AttachmentChip } from '../components/AttachmentChip';
import { Paperclip, Warning } from '../lib/icons';
import { api, ApiError } from '../lib/api';

const MAX_BYTES = 15 * 1024 * 1024;
const MAX_FILES = 10;
const MAX_TOTAL_BYTES = 15 * 1024 * 1024;
const ALLOWED_EXTS = ['.log'];
const ALLOWED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);

function isAllowed(file: File): boolean {
  if (ALLOWED_IMAGE_TYPES.has(file.type)) return true;
  if (file.type === 'application/pdf' || file.type === 'text/plain') return true;
  return ALLOWED_EXTS.some((ext) => file.name.toLowerCase().endsWith(ext));
}

interface PendingFile {
  key: string;
  file: File;
  state: 'done' | 'rejected';
  reason?: string;
}

export function Cooperate() {
  const fileInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [company, setCompany] = useState('');
  const [projectType, setProjectType] = useState('网站或 Web 应用');
  const [summary, setSummary] = useState('');
  const [detail, setDetail] = useState('');
  const [budget, setBudget] = useState('待沟通');
  const [timeframe, setTimeframe] = useState('待沟通');
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState('');
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const accepted = files.filter((entry) => entry.state === 'done');
    let acceptedCount = accepted.length;
    let acceptedBytes = accepted.reduce((total, entry) => total + entry.file.size, 0);
    const next = Array.from(list).map((file, index): PendingFile => {
      const typeAllowed = isAllowed(file);
      const sizeAllowed = file.size <= MAX_BYTES;
      const countAllowed = acceptedCount < MAX_FILES;
      const aggregateAllowed = acceptedBytes + file.size <= MAX_TOTAL_BYTES;
      const allowed = typeAllowed && sizeAllowed && countAllowed && aggregateAllowed;
      if (allowed) {
        acceptedCount += 1;
        acceptedBytes += file.size;
      }
      return {
        key: `${file.name}-${file.size}-${Date.now()}-${index}`,
        file,
        state: allowed ? 'done' : 'rejected',
        reason: !typeAllowed
          ? '不支持的文件类型'
          : !sizeAllowed
            ? '超过 15MB 限制'
            : !countAllowed
              ? '最多上传 10 个文件'
              : !aggregateAllowed
                ? '附件总量不能超过 15MB'
                : undefined,
      };
    });
    setFiles((current) => [...current, ...next]);
    if (fileInput.current) fileInput.current.value = '';
  };

  const canSubmit =
    name.trim().length >= 2 &&
    contact.trim().length >= 5 &&
    summary.trim().length >= 8 &&
    detail.trim().length >= 20 &&
    consent &&
    !submitting;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.createCooperation({
        name: name.trim(),
        contact: contact.trim(),
        company: company.trim(),
        projectType,
        summary: summary.trim(),
        detail: detail.trim(),
        budget,
        timeframe,
        consent,
        website,
        files: files
          .filter((entry) => entry.state === 'done')
          .map(({ file }) => ({ name: file.name, size: file.size, type: file.type, blob: file })),
      });
      setSubmitted(true);
    } catch (caught) {
      const apiError = caught as ApiError;
      setError(apiError.message || '提交失败，请稍后重试。');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <>
        <AppBar title="合作评估" onBack={() => window.location.assign('/')} backLabel="返回官网" />
        <main className="app-main" data-testid="cooperation-success">
          <section className="section stack intake-success">
            <h1>已提交评估</h1>
            <p className="muted">需求已发送至 AstraForge 负责人邮箱。我们查看后会通过您填写的联系方式回复。</p>
            <button type="button" className="btn btn-primary" onClick={() => window.location.assign('/')}>
              返回官网
            </button>
          </section>
        </main>
      </>
    );
  }

  const submitOnEnter = (event: KeyboardEvent<HTMLFormElement>) => {
    const target = event.target;
    if (
      event.key === 'Enter' &&
      target instanceof HTMLInputElement &&
      !['checkbox', 'file'].includes(target.type)
    ) {
      event.preventDefault();
      if (canSubmit) event.currentTarget.requestSubmit();
    }
  };

  return (
    <>
      <AppBar title="合作评估" onBack={() => window.location.assign('/')} backLabel="返回官网" />
      <main className="app-main" data-testid="cooperation-intake">
        <form className="section stack" onSubmit={submit} onKeyDown={submitOnEnter} noValidate>
          <div className="intake-heading">
            <h1>告诉我们，你想交付什么</h1>
            <p className="muted">提交后将发送至 AstraForge 负责人邮箱，用于初步评估和联系。提交不会自动启动开发，也不会产生费用。</p>
          </div>

          <div className="intake-grid">
            <div className="field">
              <label className="label" htmlFor="cooperate-name">称呼 *</label>
              <input id="cooperate-name" className="input" autoComplete="name" required minLength={2} maxLength={80} value={name} onChange={(event) => setName(event.target.value)} placeholder="怎么称呼您" />
            </div>
            <div className="field">
              <label className="label" htmlFor="cooperate-contact">联系方式 *</label>
              <input id="cooperate-contact" className="input" autoComplete="email" required minLength={5} maxLength={200} value={contact} onChange={(event) => setContact(event.target.value)} placeholder="邮箱或手机号" />
            </div>
          </div>

          <div className="intake-grid">
            <div className="field">
              <label className="label" htmlFor="cooperate-company">公司或团队</label>
              <input id="cooperate-company" className="input" autoComplete="organization" maxLength={180} value={company} onChange={(event) => setCompany(event.target.value)} placeholder="个人项目可不填" />
            </div>
            <div className="field">
              <label className="label" htmlFor="cooperate-type">项目类型</label>
              <select id="cooperate-type" className="input select" value={projectType} onChange={(event) => setProjectType(event.target.value)}>
                <option>网站或 Web 应用</option>
                <option>iOS / Android App</option>
                <option>微信小程序</option>
                <option>企业管理系统</option>
                <option>AI 智能体或自动化</option>
                <option>设计、内容或其他工具</option>
                <option>其他</option>
              </select>
            </div>
          </div>

          <div className="field">
            <label className="label" htmlFor="cooperate-summary">需求标题 *</label>
            <input id="cooperate-summary" className="input" required minLength={8} maxLength={200} value={summary} onChange={(event) => setSummary(event.target.value)} placeholder="例如：搭建一套仓储管理系统" />
            <span className="helper">至少 8 个字符，帮助我们快速识别需求。</span>
          </div>

          <div className="field">
            <label className="label" htmlFor="cooperate-detail">需求详情 *</label>
            <textarea id="cooperate-detail" className="textarea intake-detail" required minLength={20} maxLength={8000} value={detail} onChange={(event) => setDetail(event.target.value)} placeholder="请说明使用者、核心功能、期望效果，以及目前已有的资料或系统。" />
            <div className="field-meta"><span className="helper">至少 20 个字符，信息越完整，首次回复越准确。</span><span className="counter">{detail.length}/8000</span></div>
          </div>

          <div className="intake-grid">
            <div className="field">
              <label className="label" htmlFor="cooperate-budget">预算范围</label>
              <select id="cooperate-budget" className="input select" value={budget} onChange={(event) => setBudget(event.target.value)}>
                <option>待沟通</option><option>1 万元以内</option><option>1-5 万元</option><option>5-20 万元</option><option>20 万元以上</option>
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="cooperate-time">期望时间</label>
              <select id="cooperate-time" className="input select" value={timeframe} onChange={(event) => setTimeframe(event.target.value)}>
                <option>待沟通</option><option>两周内</option><option>一个月内</option><option>两个月内</option><option>三个月以上</option>
              </select>
            </div>
          </div>

          <div className="field">
            <span className="label">参考资料</span>
            <span className="helper">可选。支持图片、PDF 和日志；最多 10 个，单个及总量均不超过 15MB。</span>
            <input ref={fileInput} type="file" multiple accept="image/*,application/pdf,text/plain,.log" className="sr-only" tabIndex={-1} aria-hidden="true" onChange={(event) => addFiles(event.target.files)} />
            <button type="button" className="btn btn-secondary intake-upload" onClick={() => fileInput.current?.click()}><Paperclip weight="regular" aria-hidden="true" />选择文件</button>
            {files.length > 0 && <div className="stack-sm">{files.map((entry) => <AttachmentChip key={entry.key} name={entry.file.name} size={entry.file.size} mime={entry.file.type} state={entry.state} reason={entry.reason} onRemove={() => setFiles((current) => current.filter((item) => item.key !== entry.key))} />)}</div>}
          </div>

          <div className="honeypot" aria-hidden="true">
            <label htmlFor="cooperate-website">网站</label>
            <input id="cooperate-website" tabIndex={-1} autoComplete="off" value={website} onChange={(event) => setWebsite(event.target.value)} />
          </div>

          <label className="consent-row">
            <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />
            <span>我同意 AstraForge 为联系我和评估本次需求而通过邮件处理以上信息。未经确认，提交不会自动进入开发流程。</span>
          </label>

          {error && <div className="error-banner" role="alert"><Warning weight="regular" aria-hidden="true" /><span>{error}</span></div>}

          <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={!canSubmit}>
            {submitting ? '正在提交…' : '提交评估'}
          </button>
        </form>
      </main>
    </>
  );
}
